/**
 * Instructor Service - Funkcje do zarządzania instruktorami
 * wojskaspecjalne.pl
 */

import { supabase } from './supabase';
import type { Instructor } from '../types/database.types';

// ========================================
// TYPES
// ========================================

export type { Instructor };

export interface CreateInstructorData {
  name: string;
  slug?: string | null;
  bio?: string | null;
  email?: string | null;
  phone?: string | null;
  website_url?: string | null;
  specialization?: string[];
  experience_years?: number | null;
  photo_url?: string | null;
  is_active?: boolean;
  display_order?: number;
}

export interface UpdateInstructorData extends Partial<CreateInstructorData> {
  id: string;
}

// ========================================
// INSTRUCTORS CRUD
// ========================================

/**
 * Pobiera wszystkich instruktorów
 */
export async function getAllInstructors(options?: {
  search?: string;
  includeInactive?: boolean;
  sortByOrder?: boolean;
}): Promise<{ success: boolean; data?: Instructor[]; error?: string }> {
  try {
    let query = supabase
      .from('instructors')
      .select('*');

    // Filter by active status
    if (!options?.includeInactive) {
      query = query.eq('is_active', true);
    }

    // Search by name, bio, or specialization
    if (options?.search) {
      query = query.or(
        `name.ilike.%${options.search}%,bio.ilike.%${options.search}%`
      );
    }

    // Sort
    if (options?.sortByOrder) {
      query = query.order('display_order', { ascending: true });
    } else {
      query = query.order('name', { ascending: true });
    }

    const { data, error } = await query;

    if (error) throw error;

    return { success: true, data: data ?? [] };
  } catch (error: any) {
    console.error('Error fetching instructors:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera instruktora po ID
 */
export async function getInstructorById(
  instructorId: string
): Promise<{ success: boolean; data?: Instructor; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('instructors')
      .select('*')
      .eq('id', instructorId)
      .single();

    if (error) throw error;
    if (!data) throw new Error('Instructor not found');

    return { success: true, data };
  } catch (error: any) {
    console.error('Error fetching instructor:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera instruktora po slug
 */
export async function getInstructorBySlug(
  slug: string
): Promise<{ success: boolean; data?: Instructor; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('instructors')
      .select('*')
      .eq('slug', slug)
      .eq('is_active', true)
      .single();

    if (error) throw error;
    if (!data) throw new Error('Instructor not found');

    return { success: true, data };
  } catch (error: any) {
    console.error('Error fetching instructor by slug:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Tworzy nowego instruktora
 */
export async function createInstructor(
  instructorData: CreateInstructorData
): Promise<{ success: boolean; data?: Instructor; error?: string }> {
  try {
    // Sprawdź czy instruktor o tej nazwie już istnieje
    const { data: existing } = await supabase
      .from('instructors')
      .select('id, name')
      .ilike('name', instructorData.name)
      .single();

    if (existing) {
      return {
        success: false,
        error: `Instruktor "${instructorData.name}" już istnieje w bazie danych.`,
      };
    }

    const { data, error } = await supabase
      .from('instructors')
      .insert({
        name: instructorData.name.trim(),
        slug: instructorData.slug?.trim() || null,
        bio: instructorData.bio?.trim() || null,
        email: instructorData.email?.trim() || null,
        phone: instructorData.phone?.trim() || null,
        website_url: instructorData.website_url?.trim() || null,
        specialization: instructorData.specialization || [],
        experience_years: instructorData.experience_years || null,
        photo_url: instructorData.photo_url?.trim() || null,
        is_active: instructorData.is_active ?? true,
        display_order: instructorData.display_order || 0,
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error('Error creating instructor:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Aktualizuje instruktora
 */
export async function updateInstructor(
  instructorData: UpdateInstructorData
): Promise<{ success: boolean; data?: Instructor; error?: string }> {
  try {
    const { id, ...updateFields } = instructorData;

    // Trim string fields
    const trimmedFields: any = {};
    if (updateFields.name) trimmedFields.name = updateFields.name.trim();
    if (updateFields.slug !== undefined) trimmedFields.slug = updateFields.slug?.trim() || null;
    if (updateFields.bio !== undefined) trimmedFields.bio = updateFields.bio?.trim() || null;
    if (updateFields.email !== undefined) trimmedFields.email = updateFields.email?.trim() || null;
    if (updateFields.phone !== undefined) trimmedFields.phone = updateFields.phone?.trim() || null;
    if (updateFields.website_url !== undefined) trimmedFields.website_url = updateFields.website_url?.trim() || null;
    if (updateFields.photo_url !== undefined) trimmedFields.photo_url = updateFields.photo_url?.trim() || null;
    if (updateFields.specialization !== undefined) trimmedFields.specialization = updateFields.specialization;
    if (updateFields.experience_years !== undefined) trimmedFields.experience_years = updateFields.experience_years;
    if (updateFields.is_active !== undefined) trimmedFields.is_active = updateFields.is_active;
    if (updateFields.display_order !== undefined) trimmedFields.display_order = updateFields.display_order;

    const { data, error } = await supabase
      .from('instructors')
      .update(trimmedFields)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('Instructor not found');

    return { success: true, data };
  } catch (error: any) {
    console.error('Error updating instructor:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Usuwa instruktora
 */
export async function deleteInstructor(
  instructorId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('instructors')
      .delete()
      .eq('id', instructorId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error deleting instructor:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera statystyki instruktorów
 */
export async function getInstructorsStats(): Promise<{
  success: boolean;
  data?: {
    totalInstructors: number;
    activeInstructors: number;
    inactiveInstructors: number;
    totalSpecializations: string[];
  };
  error?: string;
}> {
  try {
    const { data: instructors, error } = await supabase
      .from('instructors')
      .select('id, is_active, specialization');

    if (error) throw error;

    const totalInstructors = instructors?.length ?? 0;
    const activeInstructors = instructors?.filter((i) => i.is_active).length ?? 0;
    const inactiveInstructors = totalInstructors - activeInstructors;

    // Zbierz wszystkie specjalizacje
    const allSpecializations = new Set<string>();
    instructors?.forEach((instructor) => {
      if (instructor.specialization && Array.isArray(instructor.specialization)) {
        instructor.specialization.forEach((spec) => {
          if (spec) allSpecializations.add(spec);
        });
      }
    });

    return {
      success: true,
      data: {
        totalInstructors,
        activeInstructors,
        inactiveInstructors,
        totalSpecializations: Array.from(allSpecializations),
      },
    };
  } catch (error: any) {
    console.error('Error fetching instructors stats:', error);
    return { success: false, error: error.message };
  }
}

